import { FC, Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleInfo,
  faPlaneDeparture,
  faPlaneArrival,
  faTimes,
  faSuitcase,
  faClipboardCheck,
  faCopy,
  faTrash,
  faCheckSquare,
  faCalendar,
} from '@fortawesome/free-solid-svg-icons';
import Button from 'components/ui/Button/Button';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import FormGroupDatepicker from 'components/ui/FormGroupDatepicker/FormGroupDatepicker';
import { Select } from 'ts/interfaces/Generic';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import useModalClass from 'hooks/useModalClass';
import { createPortal } from 'react-dom';
import { dateFormat, showError, showSuccess } from 'utils/utils';
import { MoSegmentEditorOtherResponse, MyOrder } from 'ts/shared/interfaces/MOResponse';
import { changeSegmentEditor, segmentEditorOther } from 'services/myOrders';
import ScreenshotSupport from '../../ScreenshotSupport';
import MaximizeModal from '../../MaximizeModal';
import { getStatus } from 'constants/mo';
import ConfirmDialog from '../../ConfirmDialog';
import withErrorBoundary from '../../withErrorBoundary';
import Tabs from 'components/ui/Tabs/Tabs';
import useDraggableModal from 'hooks/useDraggableModal';
import { formatISO } from 'date-fns';

interface MoFlightDetailsProps {
  myOrder?: MyOrder;
  openModal: boolean;
  setOpenModal: (open: boolean) => void;
  onSubmit: (myOrder: MyOrder) => void;
}

const MoFlightDetails: FC<MoFlightDetailsProps> = ({
  myOrder,
  openModal,
  setOpenModal,
  ...props
}) => {
  const [t] = useTranslation();
  const modalOverlayRef = useRef<HTMLDivElement>(null);
  const classList = useModalClass(openModal);
  const { modalRef, modalStyle, dragHandleProps, isDragging } = useDraggableModal(true);
  const [activeTab, setActiveTab] = useState(0);
  const [activeDetailIndex, setActiveDetailIndex] = useState(0);
  const [orderModel, setOrderModel] = useState<any>(myOrder?.order || null);
  const [segmentEditorOrder, setSegmentEditorOrder] = useState<MoSegmentEditorOtherResponse | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmCopyDetail, setShowConfirmCopyDetail] = useState(false);
  const [showConfirmDeleteDetail, setShowConfirmDeleteDetail] = useState(false);
  const [copyDetailIndex, setCopyDetailIndex] = useState<number | null>(null);
  const [deleteDetailIndex, setDeleteDetailIndex] = useState<number | null>(null);

  const toIsoString = (value: Date) => formatISO(value).replace(/([+-]\d{2}:\d{2})$/, 'Z');
  const segment = orderModel?.segments.find((s: any) => s.orderSegId === myOrder?.fullSegment.orderSegId);
  const detailsModel = segment?.details || [];
  const visibleDetailsWithIndex = detailsModel
    .map((detail: any, realIndex: number) => ({ detail, realIndex }))
    .filter(({ detail }: any) => (detail.delRow || 0) === 0);
  const selectedDetail = detailsModel[activeDetailIndex];
  const visibleDetailCount = visibleDetailsWithIndex.length;
  const statusMap = segmentEditorOrder?.activeStatus || segmentEditorOrder?.allStatus || {};
  const detailStatusOptions: Select[] = Object.entries(statusMap).map(([code, desc]) => ({
    value: code,
    label: `[${code}] ${desc}`,
  }));

  useEffect(() => {
    if (openModal && myOrder) {
      const nextOrderModel = myOrder.order;
      const segmentFromOrder = nextOrderModel.segments.find((s: any) => s.orderSegId === myOrder.fullSegment.orderSegId);
      const details = segmentFromOrder?.details || [];
      setOrderModel(nextOrderModel);
      const firstVisibleDetailIndex = details.findIndex((detail: any) => (detail.delRow || 0) === 0);
      setActiveDetailIndex(firstVisibleDetailIndex >= 0 ? firstVisibleDetailIndex : 0);
      setActiveTab(0);
    }
  }, [myOrder, openModal]);

  useEffect(() => {
    if (openModal) {
      (async () => {
        const nextSegmentEditorOrder = await segmentEditorOther();
        setSegmentEditorOrder(nextSegmentEditorOrder);
      })();
    }
  }, [openModal]);

  const updateSelectedDetail = (updater: (detail: any) => any) => {
    setOrderModel((prevOrder: any) => {
      if (!prevOrder || !myOrder) return prevOrder;
      return {
        ...prevOrder,
        segments: prevOrder.segments.map((s: any) => (
          s.orderSegId === myOrder.fullSegment.orderSegId
            ? {
              ...s,
              details: (s.details || []).map((detail: any, index: number) => (
                index === activeDetailIndex ? updater(detail) : detail
              )),
            }
            : s
        )),
      };
    });
  };

  const submitResult = (result: MyOrder) => {
    if (!myOrder) return;
    props.onSubmit({ ...result, orderRow: { ...result.orderRow, status: myOrder.orderRow.status } });
  };

  const buildOrderWithDetails = (order: any, newDetails: any[]) => {
    if (!order || !myOrder) return order;
    return {
      ...order,
      segments: order.segments.map((s: any) => (
        s.orderSegId === myOrder.fullSegment.orderSegId ? { ...s, details: newDetails } : s
      )),
    };
  };

  const syncFromResult = (result: MyOrder, preferredIndex?: number) => {
    const nextOrderModel = result.order;
    const segmentFromOrder = nextOrderModel.segments.find((s: any) => s.orderSegId === result.fullSegment.orderSegId);
    const nextDetails = segmentFromOrder?.details || [];
    setOrderModel(nextOrderModel);
    if (typeof preferredIndex === 'number' && preferredIndex >= 0 && preferredIndex < nextDetails.length) {
      setActiveDetailIndex(preferredIndex);
      return;
    }
    const firstVisibleDetailIndex = nextDetails.findIndex((detail: any) => (detail.delRow || 0) === 0);
    setActiveDetailIndex(firstVisibleDetailIndex >= 0 ? firstVisibleDetailIndex : 0);
  };

  const onClickSubmitCopyDetail = async () => {
    if (!myOrder || !orderModel || isLoading) return;
    const sourceIndex = copyDetailIndex ?? activeDetailIndex;
    const sourceDetail = detailsModel[sourceIndex];
    if (!sourceDetail) return;

    const { orderSegDetailId, id, ...detailWithoutId } = sourceDetail as any;
    const newDetails = [...detailsModel, { ...detailWithoutId, delRow: 0 }];
    const oldOrder = orderModel;
    const oldActiveDetailIndex = activeDetailIndex;
    const nextOrder = buildOrderWithDetails(orderModel, newDetails);

    setOrderModel(nextOrder);
    setActiveDetailIndex(newDetails.length - 1);
    setShowConfirmCopyDetail(false);
    setCopyDetailIndex(null);
    setIsLoading(true);

    const result = await changeSegmentEditor({
      order: nextOrder,
      changePrice: true,
    });

    if (result) {
      syncFromResult(result, newDetails.length - 1);
      submitResult(result);
    } else {
      setOrderModel(oldOrder);
      setActiveDetailIndex(oldActiveDetailIndex);
      showError(t('something_went_wrong'));
    }
    setIsLoading(false);
  };

  const onClickSubmitDeleteDetail = async () => {
    if (!myOrder || !orderModel || isLoading) return;
    if (visibleDetailCount <= 1) return;
    const sourceIndex = deleteDetailIndex ?? activeDetailIndex;
    if (sourceIndex < 0 || sourceIndex >= detailsModel.length) return;

    const newDetails = detailsModel.map((detail: any, index: number) => (
      index === sourceIndex ? { ...detail, delRow: 1 } : detail
    ));
    const oldOrder = orderModel;
    const oldActiveDetailIndex = activeDetailIndex;
    const nextOrder = buildOrderWithDetails(orderModel, newDetails);
    const nextVisibleDetailIndex = newDetails.findIndex((detail: any) => (detail.delRow || 0) === 0);
    const nextActiveIndex = nextVisibleDetailIndex >= 0 ? nextVisibleDetailIndex : 0;

    setOrderModel(nextOrder);
    setActiveDetailIndex(nextActiveIndex);
    setShowConfirmDeleteDetail(false);
    setDeleteDetailIndex(null);
    setIsLoading(true);

    const result = await changeSegmentEditor({
      order: nextOrder,
      changePrice: true,
    });

    if (result) {
      syncFromResult(result, nextActiveIndex);
      submitResult(result);
    } else {
      setOrderModel(oldOrder);
      setActiveDetailIndex(oldActiveDetailIndex);
      showError(t('something_went_wrong'));
    }
    setIsLoading(false);
  };

  const onClickSave = async () => {
    if (!myOrder || !orderModel || !segment) return;
    setIsLoading(true);
    const result = await changeSegmentEditor({
      order: orderModel,
      changePrice: true,
    });
    if (result) {
      showSuccess(t('mo.segment_gross_edited'));
      submitResult(result);
    } else {
      showError(t('something_went_wrong'));
    }
    setIsLoading(false);
  };

  return createPortal(
    <div
      className="modal-overlay modal-overlay-top mo-message-modal-overlay modal-maximizable"
      style={{ direction: 'ltr' }}
      ref={modalOverlayRef}
    >
      <div ref={modalRef} className={classList + ' modal'} style={modalStyle}>
        <div
          className={'modal-title draggable-modal-handle' + (isDragging ? ' dragging' : '')}
          {...dragHandleProps}
        >
          <h5 className="title">
            Segment Editor Details{' '}
            {myOrder?.fullSegment && (
              <Fragment>
                {' '}-{' '}Order: {myOrder.orderRow.orderId}{' '}
                Segment:
                {myOrder.fullSegment.orderSegId}{' '}
                <span className={`mo-status ${getStatus(myOrder.orderRow.status)}`}>
                  {myOrder.orderRow.status}
                </span>
              </Fragment>
            )}
          </h5>
          <div data-no-drag="true">
            <ScreenshotSupport />
            <MaximizeModal modalOverlayRef={modalOverlayRef} />
            <FontAwesomeIcon icon={faTimes} onClick={() => setOpenModal(false)} />
          </div>
        </div>
        <div className="modal-body modal-body-overflow">
          <div className="mo-block mt-10">
            <div className="mo-block-title">Details</div>
            <div className="mo-block-content flex-column">
              <div className="table-default table-small mb-10 text-sm">
                <table className="table-bordered-lines table-mobile">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>DetailId</th>
                      <th>Order Id</th>
                      <th>Seg Id</th>
                      <th>St.</th>
                      <th>Item</th>
                      <th style={{ width: '100px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleDetailsWithIndex.map(({ detail, realIndex }: any, index: number) => (
                      <tr
                        key={detail.orderSegDetailId || realIndex}
                        className={activeDetailIndex === realIndex ? 'active' : ''}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setActiveDetailIndex(realIndex)}
                      >
                        <td>{index + 1}</td>
                        <td>{detail.orderSegDetailId}</td>
                        <td>{detail.orderId}</td>
                        <td>{detail.orderSegId}</td>
                        <td>
                          <span className={`mo-status ${getStatus(detail?.sysStatus?.code || '')}`}>
                            {detail?.sysStatus?.code || ''}
                          </span>
                        </td>
                        <td>{detail?.item?.name || ''}</td>
                        <td>
                          <Button
                            title={''}
                            icon={faCopy}
                            className="btn btn-default btn-sm mr-5"
                            onClick={() => {
                              setCopyDetailIndex(realIndex);
                              setShowConfirmCopyDetail(true);
                            }}
                          />
                          {visibleDetailCount > 1 && (
                            <Button
                              title={''}
                              icon={faTrash}
                              className="btn btn-danger btn-sm"
                              onClick={() => {
                                setDeleteDetailIndex(realIndex);
                                setShowConfirmDeleteDetail(true);
                              }}
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {!!selectedDetail && (
            <div className="mt-20">
              <Tabs
        size="small"
                label="Flight detail"
                value={String(activeTab)}
                onValueChange={(v) => setActiveTab(Number(v))}
                className="mb-10 room-tabs"
                items={[
                  { value: '6', label: <FontAwesomeIcon icon={faCircleInfo} className="mr-5" /> },
                  {
                    value: '0',
                    label: (
                      <>
                        <FontAwesomeIcon icon={faCheckSquare} className="mr-5" />
                        Status & Notes
                      </>
                    ),
                  },
                  {
                    value: '1',
                    label: (
                      <>
                        <FontAwesomeIcon icon={faClipboardCheck} className="mr-5" />
                        Cancellation
                      </>
                    ),
                  },
                  {
                    value: '2',
                    label: (
                      <>
                        <FontAwesomeIcon icon={faSuitcase} className="mr-5" />
                        Item
                      </>
                    ),
                  },
                  {
                    value: '3',
                    label: (
                      <>
                        <FontAwesomeIcon icon={faPlaneDeparture} className="mr-5" />
                        Departure
                      </>
                    ),
                  },
                  {
                    value: '4',
                    label: (
                      <>
                        <FontAwesomeIcon icon={faPlaneArrival} className="mr-5" />
                        Arrival
                      </>
                    ),
                  },
                ]}
              />

              {activeTab === 0 && (
                <div>
                  <div className="mo-block mb-20">
                    <div className="mo-block-title">
                      <span>Status</span>
                    </div>
                    <div className="mo-block-content">
                      <div className="row">
                        <div className="col-md-3 col-12 mb-15">
                          <div className="mt-10">Detail Status</div>
                        </div>
                        <div className="col-md-6 col-12 mb-15">
                          <SelectCustom
                            onChange={(value) => updateSelectedDetail((detail) => ({
                              ...detail,
                              sysStatus: {
                                ...(detail.sysStatus || { code: '', descLoc: '', desc: '' }),
                                code: value,
                                desc: statusMap[value as keyof typeof statusMap] || '',
                              },
                              suppStatus: {
                                ...(detail.suppStatus || { code: '', descLoc: '', desc: '' }),
                                code: value,
                                desc: statusMap[value as keyof typeof statusMap] || '',
                              },
                            }))}
                            value={selectedDetail?.sysStatus?.code || ''}
                            options={detailStatusOptions}
                            movablePlaceholder={'Detail Status'}
                          />
                        </div>
                        <div className="col-md-3 col-12"></div>
                        <div className="col-md-3 col-6 mb-15">
                          <FormGroup
                            onChange={() => { }}
                            value={selectedDetail?.sysStatus?.code || ''}
                            label={'Sys Status Code'}
                            movablePlaceholder={'Sys Status Code'}
                            disabled={true}
                          />
                        </div>
                        <div className="col-md-3 col-6 mb-15">
                          <FormGroup
                            onChange={() => { }}
                            value={selectedDetail?.sysStatus?.desc || ''}
                            label={'Sys Status Desc'}
                            movablePlaceholder={'Sys Status Desc'}
                            disabled={true}
                          />
                        </div>
                        <div className="col-md-3 col-6 mb-15">
                          <FormGroup
                            onChange={() => { }}
                            value={selectedDetail?.suppStatus?.code || ''}
                            label={'Supp Status Code'}
                            movablePlaceholder={'Supp Status Code'}
                            disabled={true}
                          />
                        </div>
                        <div className="col-md-3 col-6 mb-15">
                          <FormGroup
                            onChange={() => { }}
                            value={selectedDetail?.suppStatus?.desc || ''}
                            label={'Supp Status Desc'}
                            movablePlaceholder={'Supp Status Desc'}
                            disabled={true}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mo-block">
                    <div className="mo-block-title">
                      <span>Notes</span>
                    </div>
                    <div className="mo-block-content">
                      <div className="row">
                        <div className="col-md-3 col-12 mb-15">
                          <div className="mt-10">Internal Note</div>
                        </div>
                        <div className="col-md-9 col-12 mb-15">
                          <textarea
                            className="form-control form-control-textarea"
                            value={selectedDetail?.internalNote || ''}
                            rows={3}
                            onChange={(e) => updateSelectedDetail((detail) => ({ ...detail, internalNote: e.target.value }))}
                          ></textarea>
                        </div>
                        <div className="col-md-3 col-12 mb-15">
                          <div className="mt-10">Public Note</div>
                        </div>
                        <div className="col-md-9 col-12 mb-15">
                          <textarea
                            className="form-control form-control-textarea"
                            value={selectedDetail?.publicNote || ''}
                            rows={3}
                            onChange={(e) => updateSelectedDetail((detail) => ({ ...detail, publicNote: e.target.value }))}
                          ></textarea>
                        </div>
                        <div className="col-md-3 col-12 mb-15">
                          <div className="mt-10">Sys Note</div>
                        </div>
                        <div className="col-md-9 col-12 mb-15">
                          <textarea
                            className="form-control form-control-textarea"
                            value={typeof selectedDetail?.sysNote === 'string' ? selectedDetail.sysNote : ''}
                            rows={3}
                            onChange={(e) => updateSelectedDetail((detail) => ({ ...detail, sysNote: e.target.value }))}
                          ></textarea>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 6 && (
                <div className="mo-block-content">
                  <div className="row">
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={() => { }}
                        value={selectedDetail?.roomPaxString || ''}
                        label={'Room Pax String'}
                        movablePlaceholder={'Room Pax String'}
                        disabled={true}
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={() => { }}
                        value={selectedDetail?.orderId?.toString() || ''}
                        label={'Order Id'}
                        movablePlaceholder={'Order Id'}
                        disabled={true}
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={() => { }}
                        value={selectedDetail?.orderSegId?.toString() || ''}
                        label={'Order Seg Id'}
                        movablePlaceholder={'Order Seg Id'}
                        disabled={true}
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={() => { }}
                        value={selectedDetail?.orderSegDetailId?.toString() || ''}
                        label={'Order Seg Detail Id'}
                        movablePlaceholder={'Order Seg Detail Id'}
                        disabled={true}
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={() => { }}
                        value={selectedDetail?.userCodeC?.toString() || ''}
                        label={'User Code C'}
                        movablePlaceholder={'User Code C'}
                        disabled={true}
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={() => { }}
                        value={selectedDetail?.userCodeU?.toString() || ''}
                        label={'User Code U'}
                        movablePlaceholder={'User Code U'}
                        disabled={true}
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={() => { }}
                        value={selectedDetail?.dateC ? dateFormat(selectedDetail.dateC, 'dd/MM/yy HH:mm') : ''}
                        label={'Date C'}
                        movablePlaceholder={'Date C'}
                        disabled={true}
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={() => { }}
                        value={selectedDetail?.dateU ? dateFormat(selectedDetail.dateU, 'dd/MM/yy HH:mm') : ''}
                        label={'Date U'}
                        movablePlaceholder={'Date U'}
                        disabled={true}
                      />
                    </div>
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroup
                        onChange={() => { }}
                        value={selectedDetail?.exteranlId_1 || ''}
                        label={'ExteranlId 1'}
                        movablePlaceholder={'ExteranlId 1'}
                        disabled={true}
                      />
                    </div>
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroup
                        onChange={() => { }}
                        value={selectedDetail?.exteranlId_2 || ''}
                        label={'ExteranlId 2'}
                        movablePlaceholder={'ExteranlId 2'}
                        disabled={true}
                      />
                    </div>
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroup
                        onChange={() => { }}
                        value={selectedDetail?.bToken || ''}
                        label={'BToken'}
                        movablePlaceholder={'BToken'}
                        disabled={true}
                      />
                    </div>
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroup
                        onChange={() => { }}
                        value={selectedDetail?.suppKey || ''}
                        label={'Supp Key'}
                        movablePlaceholder={'Supp Key'}
                        disabled={true}
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 1 && (
                <div className="mo-block-content">
                  <div className="row">
                    <div className="col-md-3 col-12 mb-15">
                      <div className="mt-10">Cancellation Date</div>
                    </div>
                    <div className="col-md-9 col-12 mb-15 ">
                      <FormGroupDatepicker
                        onChange={(value) => updateSelectedDetail((detail) => ({
                          ...detail,
                          cancellationDateLimit: toIsoString(value),
                        }))}
                        value={new Date(selectedDetail?.cancellationDateLimit || '')}
                        label={'Cancellation Date'}
                        movablePlaceholder={'Cancellation Date'}
                        movablePlaceholderIcon={faCalendar}
                        appendToBody
                      />
                    </div>
                    <div className="col-md-3 col-12 mb-15">
                      <div className="mt-10">Cancellation Time</div>
                    </div>
                    <div className="col-md-9 col-12 mb-15">
                      <FormGroup
                        onChange={(value) => updateSelectedDetail((detail) => ({ ...detail, cancellationTimeLimit: value }))}
                        value={selectedDetail?.cancellationTimeLimit || ''}
                        label={'Cancellation Time'}
                        movablePlaceholder={'Cancellation Time'}
                      />
                    </div>
                    <div className="col-md-3 col-12 mb-15">
                      <div className="mt-10">Cancellation Note</div>
                    </div>
                    <div className="col-md-9 col-12 mb-15">
                      <textarea
                        className="form-control form-control-textarea"
                        value={selectedDetail?.cancellationNote || ''}
                        rows={7}
                        onChange={(e) => updateSelectedDetail((detail) => ({ ...detail, cancellationNote: e.target.value }))}
                      ></textarea>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 2 && (
                <div className="mo-block-content">
                  <div className="row">
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={(value) => updateSelectedDetail((detail) => ({
                          ...detail,
                          item: { ...(detail.item || { code: '', name: '', id: 0, sCode: '' }), code: value },
                        }))}
                        value={selectedDetail?.item?.code || ''}
                        label={'Item Code'}
                        movablePlaceholder={'Item Code'}
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={(value) => updateSelectedDetail((detail) => ({
                          ...detail,
                          item: { ...(detail.item || { code: '', name: '', id: 0, sCode: '' }), name: value },
                        }))}
                        value={selectedDetail?.item?.name || ''}
                        label={'Item Name'}
                        movablePlaceholder={'Item Name'}
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={(value) => updateSelectedDetail((detail) => ({
                          ...detail,
                          itemType: { ...(detail.itemType || { code: '', name: '', id: 0, sCode: '' }), code: value },
                        }))}
                        value={selectedDetail?.itemType?.code || ''}
                        label={'Item Type Code'}
                        movablePlaceholder={'Item Type Code'}
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={(value) => updateSelectedDetail((detail) => ({
                          ...detail,
                          itemType: { ...(detail.itemType || { code: '', name: '', id: 0, sCode: '' }), name: value },
                        }))}
                        value={selectedDetail?.itemType?.name || ''}
                        label={'Item Type Name'}
                        movablePlaceholder={'Item Type Name'}
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={(value) => updateSelectedDetail((detail) => ({ ...detail, legNumber: Number(value) || 0 }))}
                        value={selectedDetail?.legNumber?.toString() || ''}
                        label={'Leg Number'}
                        movablePlaceholder={'Leg Number'}
                        allowOnlyNumbers
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={(value) => updateSelectedDetail((detail) => ({ ...detail, flightNumber: value }))}
                        value={selectedDetail?.flightNumber || ''}
                        label={'Flight Number'}
                        movablePlaceholder={'Flight Number'}
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={(value) => updateSelectedDetail((detail) => ({
                          ...detail,
                          operatingItem: {
                            ...(detail.operatingItem || { code: '', name: '', id: 0, sCode: '' }),
                            code: value,
                          },
                        }))}
                        value={selectedDetail?.operatingItem?.code || ''}
                        label={'Op. Item Code'}
                        movablePlaceholder={'Op. Item Code'}
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={(value) => updateSelectedDetail((detail) => ({
                          ...detail,
                          operatingItem: {
                            ...(detail.operatingItem || { code: '', name: '', id: 0, sCode: '' }),
                            name: value,
                          },
                        }))}
                        value={selectedDetail?.operatingItem?.name || ''}
                        label={'Op. Item Name'}
                        movablePlaceholder={'Op. Item Name'}
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={(value) => updateSelectedDetail((detail) => ({ ...detail, elapsedTime: value }))}
                        value={selectedDetail?.elapsedTime || ''}
                        label={'Elapsed Time'}
                        movablePlaceholder={'Elapsed Time'}
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={(value) => updateSelectedDetail((detail) => ({ ...detail, itemPNR: value }))}
                        value={selectedDetail?.itemPNR || ''}
                        label={'Item PNR'}
                        movablePlaceholder={'Item PNR'}
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={(value) => updateSelectedDetail((detail) => ({ ...detail, numberOfPax: Number(value) || 0 }))}
                        value={selectedDetail?.numberOfPax?.toString() || ''}
                        label={'Number Of Pax'}
                        movablePlaceholder={'Number Of Pax'}
                        allowOnlyNumbers
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={(value) => updateSelectedDetail((detail) => ({ ...detail, miles: Number(value) || 0 }))}
                        value={selectedDetail?.miles?.toString() || ''}
                        label={'Miles'}
                        movablePlaceholder={'Miles'}
                        allowOnlyNumbers
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 3 && (
                <div className="mo-block-content">
                  <div className="row">
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroupDatepicker
                        onChange={(value) => updateSelectedDetail((detail) => ({
                          ...detail,
                          departure: { ...(detail.departure || {}), date: toIsoString(value) },
                        }))}
                        value={new Date(selectedDetail?.departure?.date || '')}
                        label={'Date'}
                        movablePlaceholder={'Date'}
                        movablePlaceholderIcon={faCalendar}
                        appendToBody
                      />
                    </div>
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroup
                        onChange={(value) => updateSelectedDetail((detail) => ({
                          ...detail,
                          departure: { ...(detail.departure || {}), time: value },
                        }))}
                        value={selectedDetail?.departure?.time || ''}
                        label={'Time'}
                        movablePlaceholder={'Time'}
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={(value) => updateSelectedDetail((detail) => ({
                          ...detail,
                          departure: {
                            ...(detail.departure || {}),
                            port: { ...(detail.departure?.port || { id: 0, code: '', sCode: '', name: '' }), code: value },
                          },
                        }))}
                        value={selectedDetail?.departure?.port?.code || ''}
                        label={'Dep Port'}
                        movablePlaceholder={'Dep Port'}
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={(value) => updateSelectedDetail((detail) => ({
                          ...detail,
                          departure: {
                            ...(detail.departure || {}),
                            port: { ...(detail.departure?.port || { id: 0, code: '', sCode: '', name: '' }), name: value },
                          },
                        }))}
                        value={selectedDetail?.departure?.port?.name || ''}
                        label={'Port Name'}
                        movablePlaceholder={'Port Name'}
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={(value) => updateSelectedDetail((detail) => ({
                          ...detail,
                          departure: {
                            ...(detail.departure || {}),
                            classOfService: {
                              ...(detail.departure?.classOfService || { id: 0, code: '', sCode: '', name: '' }),
                              code: value,
                            },
                          },
                        }))}
                        value={selectedDetail?.departure?.classOfService?.code || ''}
                        label={'Cls. Of Serv. Code'}
                        movablePlaceholder={'Cls. Of Serv. Code'}
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={(value) => updateSelectedDetail((detail) => ({
                          ...detail,
                          departure: {
                            ...(detail.departure || {}),
                            classOfService: {
                              ...(detail.departure?.classOfService || { id: 0, code: '', sCode: '', name: '' }),
                              name: value,
                            },
                          },
                        }))}
                        value={selectedDetail?.departure?.classOfService?.name || ''}
                        label={'Cls. Of Serv. Name'}
                        movablePlaceholder={'Cls. Of Serv. Name'}
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={(value) => updateSelectedDetail((detail) => ({
                          ...detail,
                          departure: {
                            ...(detail.departure || {}),
                            company: { ...(detail.departure?.company || { id: 0, code: '', sCode: '', name: '' }), code: value },
                          },
                        }))}
                        value={selectedDetail?.departure?.company?.code || ''}
                        label={'Company Code'}
                        movablePlaceholder={'Company Code'}
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={(value) => updateSelectedDetail((detail) => ({
                          ...detail,
                          departure: {
                            ...(detail.departure || {}),
                            company: { ...(detail.departure?.company || { id: 0, code: '', sCode: '', name: '' }), name: value },
                          },
                        }))}
                        value={selectedDetail?.departure?.company?.name || ''}
                        label={'Company Name'}
                        movablePlaceholder={'Company Name'}
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={(value) => updateSelectedDetail((detail) => ({
                          ...detail,
                          departure: {
                            ...(detail.departure || {}),
                            operatingCompany: {
                              ...(detail.departure?.operatingCompany || { id: 0, code: '', sCode: '', name: '' }),
                              code: value,
                            },
                          },
                        }))}
                        value={selectedDetail?.departure?.operatingCompany?.code || ''}
                        label={'Op. Com. Code'}
                        movablePlaceholder={'Op. Com. Code'}
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={(value) => updateSelectedDetail((detail) => ({
                          ...detail,
                          departure: {
                            ...(detail.departure || {}),
                            operatingCompany: {
                              ...(detail.departure?.operatingCompany || { id: 0, code: '', sCode: '', name: '' }),
                              name: value,
                            },
                          },
                        }))}
                        value={selectedDetail?.departure?.operatingCompany?.name || ''}
                        label={'Op. Com. Name'}
                        movablePlaceholder={'Op. Com. Name'}
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={(value) => updateSelectedDetail((detail) => ({
                          ...detail,
                          departure: { ...(detail.departure || {}), terminal: value },
                        }))}
                        value={selectedDetail?.departure?.terminal || ''}
                        label={'Terminal'}
                        movablePlaceholder={'Terminal'}
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={(value) => updateSelectedDetail((detail) => ({
                          ...detail,
                          departure: { ...(detail.departure || {}), fareBase: value },
                        }))}
                        value={selectedDetail?.departure?.fareBase || ''}
                        label={'Fare Base'}
                        movablePlaceholder={'Fare Base'}
                      />
                    </div>
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroup
                        onChange={(value) => updateSelectedDetail((detail) => ({
                          ...detail,
                          departure: { ...(detail.departure || {}), rule: value },
                        }))}
                        value={selectedDetail?.departure?.rule || ''}
                        label={'Rule'}
                        movablePlaceholder={'Rule'}
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 4 && (
                <div className="mo-block-content">
                  <div className="row">
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroupDatepicker
                        onChange={(value) => updateSelectedDetail((detail) => ({
                          ...detail,
                          arrival: { ...(detail.arrival || {}), date: toIsoString(value) },
                        }))}
                        value={new Date(selectedDetail?.arrival?.date || '')}
                        label={'Date'}
                        movablePlaceholder={'Date'}
                        movablePlaceholderIcon={faCalendar}
                        appendToBody
                      />
                    </div>
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroup
                        onChange={(value) => updateSelectedDetail((detail) => ({
                          ...detail,
                          arrival: { ...(detail.arrival || {}), time: value },
                        }))}
                        value={selectedDetail?.arrival?.time || ''}
                        label={'Time'}
                        movablePlaceholder={'Time'}
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={(value) => updateSelectedDetail((detail) => ({
                          ...detail,
                          arrival: {
                            ...(detail.arrival || {}),
                            port: { ...(detail.arrival?.port || { id: 0, code: '', sCode: '', name: '' }), code: value },
                          },
                        }))}
                        value={selectedDetail?.arrival?.port?.code || ''}
                        label={'Arr Port'}
                        movablePlaceholder={'Arr Port'}
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={(value) => updateSelectedDetail((detail) => ({
                          ...detail,
                          arrival: {
                            ...(detail.arrival || {}),
                            port: { ...(detail.arrival?.port || { id: 0, code: '', sCode: '', name: '' }), name: value },
                          },
                        }))}
                        value={selectedDetail?.arrival?.port?.name || ''}
                        label={'Port Name'}
                        movablePlaceholder={'Port Name'}
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={(value) => updateSelectedDetail((detail) => ({
                          ...detail,
                          arrival: {
                            ...(detail.arrival || {}),
                            classOfService: {
                              ...(detail.arrival?.classOfService || { id: 0, code: '', sCode: '', name: '' }),
                              code: value,
                            },
                          },
                        }))}
                        value={selectedDetail?.arrival?.classOfService?.code || ''}
                        label={'Cls. Of Serv. Code'}
                        movablePlaceholder={'Cls. Of Serv. Code'}
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={(value) => updateSelectedDetail((detail) => ({
                          ...detail,
                          arrival: {
                            ...(detail.arrival || {}),
                            classOfService: {
                              ...(detail.arrival?.classOfService || { id: 0, code: '', sCode: '', name: '' }),
                              name: value,
                            },
                          },
                        }))}
                        value={selectedDetail?.arrival?.classOfService?.name || ''}
                        label={'Cls. Of Serv. Name'}
                        movablePlaceholder={'Cls. Of Serv. Name'}
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={(value) => updateSelectedDetail((detail) => ({
                          ...detail,
                          arrival: {
                            ...(detail.arrival || {}),
                            company: { ...(detail.arrival?.company || { id: 0, code: '', sCode: '', name: '' }), code: value },
                          },
                        }))}
                        value={selectedDetail?.arrival?.company?.code || ''}
                        label={'Company Code'}
                        movablePlaceholder={'Company Code'}
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={(value) => updateSelectedDetail((detail) => ({
                          ...detail,
                          arrival: {
                            ...(detail.arrival || {}),
                            company: { ...(detail.arrival?.company || { id: 0, code: '', sCode: '', name: '' }), name: value },
                          },
                        }))}
                        value={selectedDetail?.arrival?.company?.name || ''}
                        label={'Company Name'}
                        movablePlaceholder={'Company Name'}
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={(value) => updateSelectedDetail((detail) => ({
                          ...detail,
                          arrival: {
                            ...(detail.arrival || {}),
                            operatingCompany: {
                              ...(detail.arrival?.operatingCompany || { id: 0, code: '', sCode: '', name: '' }),
                              code: value,
                            },
                          },
                        }))}
                        value={selectedDetail?.arrival?.operatingCompany?.code || ''}
                        label={'Op. Com. Code'}
                        movablePlaceholder={'Op. Com. Code'}
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={(value) => updateSelectedDetail((detail) => ({
                          ...detail,
                          arrival: {
                            ...(detail.arrival || {}),
                            operatingCompany: {
                              ...(detail.arrival?.operatingCompany || { id: 0, code: '', sCode: '', name: '' }),
                              name: value,
                            },
                          },
                        }))}
                        value={selectedDetail?.arrival?.operatingCompany?.name || ''}
                        label={'Op. Com. Name'}
                        movablePlaceholder={'Op. Com. Name'}
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={(value) => updateSelectedDetail((detail) => ({
                          ...detail,
                          arrival: { ...(detail.arrival || {}), terminal: value },
                        }))}
                        value={selectedDetail?.arrival?.terminal || ''}
                        label={'Terminal'}
                        movablePlaceholder={'Terminal'}
                      />
                    </div>
                    <div className="col-md-3 col-6 mb-15">
                      <FormGroup
                        onChange={(value) => updateSelectedDetail((detail) => ({
                          ...detail,
                          arrival: { ...(detail.arrival || {}), fareBase: value },
                        }))}
                        value={selectedDetail?.arrival?.fareBase || ''}
                        label={'Fare Base'}
                        movablePlaceholder={'Fare Base'}
                      />
                    </div>
                    <div className="col-md-6 col-12 mb-15">
                      <FormGroup
                        onChange={(value) => updateSelectedDetail((detail) => ({
                          ...detail,
                          arrival: { ...(detail.arrival || {}), rule: value },
                        }))}
                        value={selectedDetail?.arrival?.rule || ''}
                        label={'Rule'}
                        movablePlaceholder={'Rule'}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <Button disabled={isLoading} title={t('save')} className="btn btn-primary ml-0 mr-a" onClick={onClickSave} />
          <Button
            title={t('cancel')}
            className="btn btn-default"
            onClick={() => setOpenModal(false)}
          />
        </div>
        {showConfirmCopyDetail && (
          <ConfirmDialog
            title="Copy Detail"
            openModal={showConfirmCopyDetail}
            close={() => {
              setShowConfirmCopyDetail(false);
              setCopyDetailIndex(null);
            }}
            onConfirm={() => onClickSubmitCopyDetail()}
          />
        )}
        {showConfirmDeleteDetail && (
          <ConfirmDialog
            title="Delete Detail"
            openModal={showConfirmDeleteDetail}
            close={() => {
              setShowConfirmDeleteDetail(false);
              setDeleteDetailIndex(null);
            }}
            onConfirm={() => onClickSubmitDeleteDetail()}
          />
        )}
      </div>
    </div>,
    document.body,
  );
};

export default withErrorBoundary(MoFlightDetails, 'MoFlightDetails');
